/**
 * Каталог ВБ — описание колонок таблицы.
 *
 * Вынесено из страницы: колонок восемнадцать, и вперемешку с логикой загрузки
 * они читались бы плохо. Видимость и порядок настраиваются пользователем,
 * здесь задаются только исходные значения.
 */

import React from 'react'
import { Badge, type BadgeVariant } from '../../../components/ui/Badge'
import { ImagePreviewPlaceholder, ImagePreviewThumb } from '../../../components/ui/ImagePreviewThumb'
import { marketplaceThumbnailUrl } from '../../../shared/media/marketplaceThumbnails'
import type { CatalogColumn } from '../../marketplace-catalog'
import type { WbCatalogProduct } from './types'

/** WB отдаёт только два состояния карточки: активна и в корзине. */
export const wbStatusLabel = (isInTrash?: boolean) => (isInTrash ? 'В корзине' : 'Активный')
export const wbStatusVariant = (isInTrash?: boolean): BadgeVariant =>
  isInTrash ? 'gray' : 'green'

/**
 * Статус строки каталога. Блокировка WB важнее корзины и активности: правки
 * такой карточки площадка отклоняет, о ней PIM узнаёт из отказа.
 */
export const wbRowStatus = (product: WbCatalogProduct): { label: string; variant: BadgeVariant; title?: string } =>
  product.banned
    ? { label: 'Заблокирована', variant: 'red', title: product.banned.reason || 'WB отклоняет правки карточки' }
    : { label: wbStatusLabel(product.is_in_trash), variant: wbStatusVariant(product.is_in_trash) }

export const nmIdOf = (product: WbCatalogProduct): string =>
  String(product.nmID ?? product.nm_id ?? '')

export const vendorCodeOf = (product: WbCatalogProduct): string =>
  String(product.vendorCode ?? product.vendor_code ?? '')

/** Первое доступное фото карточки: WB отдаёт несколько размеров, берём средний. */
export const photoUrlOf = (product: WbCatalogProduct): string => {
  const photo: any = product.photos?.[0]
  if (!photo) return ''
  if (typeof photo === 'string') return photo
  return photo.c246x328 || photo.square || photo.tm || photo.big || ''
}

/**
 * Итоговая цена карточки с учётом скидки продавца.
 * Округляем: WB торгует целыми рублями, а «656,5 ₽» в списке выглядит ошибкой.
 */
export const priceOf = (product: WbCatalogProduct) => {
  const base = Number(product.price?.base_price ?? 0)
  const discount = Number(product.price?.discount ?? 0)
  return { base, discount, final: Math.round(base - (base * discount) / 100) }
}

export const formatMoney = (value: number): string => `${value.toLocaleString('ru-RU')} ₽`

export const formatDateTime = (value?: string | null): string =>
  value
    ? new Date(value).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

export const creationDateOf = (product: WbCatalogProduct) => {
  const value = product.effective_created_at || null
  const pimTime = product.pim_created_at ? new Date(product.pim_created_at).getTime() : Number.NaN
  const effectiveTime = value ? new Date(value).getTime() : Number.NaN
  return {
    value,
    fromPim: Number.isFinite(pimTime) && pimTime === effectiveTime,
  }
}

const dash = <span className="text-gray-300 dark:text-zinc-600">—</span>

const Thumb: React.FC<{ product: WbCatalogProduct }> = ({ product }) => {
  const url = photoUrlOf(product)
  // Размер превью по умолчанию — вертикальный, как в «Каталоге макетов v2»:
  // фото карточки маркетплейса и само по себе вертикальное (246×328).
  if (!url) return <ImagePreviewPlaceholder />
  // В зеркале `photos` чаще лежит массивом строк, и это адреса `big` — на
  // плитку идёт кадр 900 пикселей. Корзина отдаёт готовый c246x328.
  return <ImagePreviewThumb src={marketplaceThumbnailUrl(url, 246)} alt={product.title || ''} />
}

export const WB_COLUMNS: Array<CatalogColumn<WbCatalogProduct>> = [
  {
    key: 'product',
    label: 'Товар',
    width: 360,
    minWidth: 260,
    sortColumn: 'title',
    locked: true,
    render: (product, { mode }) => (
      <span className="flex min-w-0 items-center gap-2.5">
        {mode === 'standard' && <Thumb product={product} />}
        <span className="min-w-0 flex-1">
          <span
            className="block truncate text-[0.8125rem] text-gray-900 dark:text-zinc-100"
            title={product.title || product.subjectName || product.subject_name}
          >
            {product.title || product.subjectName || product.subject_name || 'Без названия'}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5">
            <span className="shrink-0 font-mono text-xs text-gray-500 dark:text-zinc-400">
              {vendorCodeOf(product) || '—'}
            </span>
            {product.brand && (
              <span className="truncate text-xs text-gray-500 dark:text-zinc-400">
                · {product.brand}
              </span>
            )}
          </span>
        </span>
      </span>
    ),
  },
  {
    key: 'vendorCode',
    label: 'Артикул',
    width: 150,
    minWidth: 110,
    sortColumn: 'vendor_code',
    render: (product) => (
      <span className="block truncate font-mono text-[0.8125rem]">
        {vendorCodeOf(product) || '—'}
      </span>
    ),
  },
  {
    key: 'brand',
    label: 'Бренд',
    width: 150,
    minWidth: 110,
    sortColumn: 'brand',
    render: (product) =>
      product.brand ? (
        <span className="block truncate" title={product.brand}>
          {product.brand}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'subject',
    label: 'Категория',
    width: 165,
    minWidth: 120,
    sortColumn: 'subject_name',
    render: (product) => {
      const value = product.subjectName || product.subject_name
      return value ? (
        <span className="block truncate text-gray-700 dark:text-zinc-300" title={value}>
          {value}
        </span>
      ) : (
        dash
      )
    },
  },
  {
    key: 'nmID',
    label: 'NM ID',
    width: 120,
    minWidth: 100,
    sortColumn: 'nm_id',
    render: (product) => {
      const value = nmIdOf(product)
      return value ? (
        <span className="block truncate font-mono text-xs text-blue-600 dark:text-blue-400">
          {value}
        </span>
      ) : (
        dash
      )
    },
  },
  {
    key: 'price',
    label: 'Цена',
    width: 120,
    minWidth: 95,
    sortColumn: 'price',
    align: 'right',
    render: (product) => {
      const { base, discount, final } = priceOf(product)
      if (base <= 0) return dash
      return (
        <span className="block">
          {discount > 0 && (
            <span className="block text-xs text-gray-400 line-through dark:text-zinc-500">
              {formatMoney(base)}
            </span>
          )}
          <span className="block font-semibold tabular-nums">{formatMoney(final)}</span>
        </span>
      )
    },
  },
  {
    key: 'discount',
    label: 'Скидка',
    width: 100,
    minWidth: 85,
    sortColumn: 'discount',
    align: 'right',
    render: (product) => {
      const discount = Number(product.price?.discount ?? 0)
      return discount > 0 ? <Badge variant="red">-{discount}%</Badge> : dash
    },
  },
  {
    key: 'spp',
    label: 'СПП',
    width: 90,
    minWidth: 75,
    align: 'right',
    render: (product) => {
      const spp = product.price_index?.spp
      return spp === undefined || spp === null ? dash : (
        <span className="tabular-nums text-gray-700 dark:text-zinc-300">{spp}%</span>
      )
    },
  },
  {
    key: 'sizes',
    label: 'Размеры',
    width: 100,
    minWidth: 85,
    align: 'center',
    render: (product) => {
      const count = product.sizesCount ?? product.sizes?.length ?? 0
      return count > 0 ? <Badge variant="blue">{count}</Badge> : dash
    },
  },
  {
    key: 'characteristics',
    label: 'Характеристики',
    width: 130,
    minWidth: 105,
    align: 'center',
    render: (product) => {
      const count = product.characteristicsCount ?? product.characteristics?.length ?? 0
      return count > 0 ? (
        <span className="tabular-nums text-gray-700 dark:text-zinc-300">{count}</span>
      ) : (
        dash
      )
    },
  },
  {
    key: 'kiz',
    label: 'КИЗ',
    width: 90,
    minWidth: 75,
    align: 'center',
    render: (product) =>
      product.needKiz || product.need_kiz ? <Badge variant="yellow">Да</Badge> : dash,
  },
  {
    key: 'pim',
    label: 'PIM',
    width: 130,
    minWidth: 100,
    align: 'center',
    render: (product) =>
      product.pim_article ? (
        <span className="block truncate font-mono text-xs text-indigo-700 dark:text-indigo-300"
              title={product.pim_title || product.pim_article}>
          {product.pim_article}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'status',
    label: 'Статус',
    width: 120,
    minWidth: 100,
    align: 'center',
    render: (product) => {
      const status = wbRowStatus(product)
      return (
        <span title={status.title}>
          <Badge variant={status.variant} dot>
            {status.label}
          </Badge>
        </span>
      )
    },
  },
  {
    key: 'createdAt',
    label: 'Дата создания',
    width: 145,
    minWidth: 120,
    sortColumn: 'created_at',
    render: (product) => {
      const creationDate = creationDateOf(product)
      return (
        <span
          className="block whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400"
          title={creationDate.fromPim ? 'Создано конвейером PIM' : undefined}
        >
          {formatDateTime(creationDate.value)}
        </span>
      )
    },
  },
  {
    key: 'updatedAt',
    label: 'Обновлено',
    width: 145,
    minWidth: 120,
    sortColumn: 'updated_at',
    render: (product) => (
      <span className="block whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400">
        {formatDateTime(product.updated_at || product.updatedAt)}
      </span>
    ),
  },
  {
    key: 'lastSync',
    label: 'Синхронизировано',
    width: 150,
    minWidth: 125,
    render: (product) => (
      <span className="block whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400">
        {formatDateTime(product.last_sync_at || product.lastSyncAt)}
      </span>
    ),
  },
  {
    key: 'imtID',
    label: 'IMT ID',
    width: 120,
    minWidth: 100,
    render: (product) => {
      const value = product.imtID ?? product.imt_id
      return value ? <span className="block truncate font-mono text-xs">{value}</span> : dash
    },
  },
  {
    key: 'subjectID',
    label: 'Subject ID',
    width: 110,
    minWidth: 95,
    render: (product) => {
      const value = product.subjectID ?? product.subject_id
      return value ? <span className="block truncate font-mono text-xs">{value}</span> : dash
    },
  },
]

/** Колонки, видимые до того как пользователь настроит своё */
export const WB_DEFAULT_VISIBLE = [
  'product',
  'brand',
  'subject',
  'nmID',
  'price',
  'discount',
  'pim',
  'status',
  'createdAt',
]

/** Колонки выгрузки в XLS — идентификаторы WB, а не «общие» названия */
export const WB_XLS_COLUMNS = [
  { key: 'vendorCode', header: 'Артикул', value: (p: WbCatalogProduct) => vendorCodeOf(p) },
  { key: 'nmID', header: 'NM ID', value: (p: WbCatalogProduct) => nmIdOf(p) },
  { key: 'title', header: 'Название', value: (p: WbCatalogProduct) => p.title || '' },
  { key: 'brand', header: 'Бренд', value: (p: WbCatalogProduct) => p.brand || '' },
  {
    key: 'subjectName',
    header: 'Категория',
    value: (p: WbCatalogProduct) => p.subjectName || p.subject_name || '',
  },
  { key: 'price', header: 'Цена', value: (p: WbCatalogProduct) => priceOf(p).final || '' },
  {
    key: 'discount',
    header: 'Скидка, %',
    value: (p: WbCatalogProduct) => p.price?.discount ?? '',
  },
  { key: 'spp', header: 'СПП, %', value: (p: WbCatalogProduct) => p.price_index?.spp ?? '' },
  { key: 'pim', header: 'Артикул PIM', value: (p: WbCatalogProduct) => p.pim_article || '' },
  {
    key: 'status',
    header: 'Статус',
    value: (p: WbCatalogProduct) => wbRowStatus(p).label,
  },
  {
    key: 'createdAt',
    header: 'Дата создания',
    value: (p: WbCatalogProduct) => formatDateTime(p.effective_created_at),
  },
  {
    key: 'updatedAt',
    header: 'Обновлено',
    value: (p: WbCatalogProduct) => formatDateTime(p.updated_at || p.updatedAt),
  },
]
